import React, { useState, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// User Master. Who reaches this screen is decided the way it is for every other screen: a
// department holds it, and the role's operations -- narrowed per person by the Screen Access
// grid -- say what may be done here. It used to be the SuperAdmin's and the Admin's alone.
//
// A user only ever sees the accounts in their own departments, and never a SuperAdmin. That is
// the backend's doing, in visibleUsers -- hiding a row here would stop nobody who can type a URL.
const SUPERADMIN_ROLE = 0;

function List() {

    const [userList, setUserList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [loadError, setLoadError] = useState('');

    // The sketch's filter row: Name, Department (multi), Role (multi), Mobile, Center / MHT ID,
    // Status.
    const [searchQuery, setSearchQuery] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState([]);
    const [roleFilter, setRoleFilter] = useState([]);
    const [mobileQuery, setMobileQuery] = useState('');
    const [centerQuery, setCenterQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });

    const role = String(localStorage.getItem('userRole'));
    // Screen Access belongs to the SuperAdmin. Role 0, not an operation: it is not "may you
    // delete things", it is "are you the account that decides what everyone else may do".
    const isSuperadminSession = role === '0';
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');
    // A department POC or admin can be given this screen with View alone, to look up their own
    // department's logins. Everything that writes is hidden from them, and Edit becomes View.
    const canUpdate = can('update');
    const canCreate = can('create');

    const navigate = useNavigate();

    useEffect(() => {
        getUserList();
        getOptions();
    }, []);

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const mobile = mobileQuery.trim().toLowerCase();
        const center = centerQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        const wantedDepartments = departmentFilter.map((department) => String(department.value));
        const wantedRoles = roleFilter.map((option) => String(option.value));

        setFilteredList(userList.filter((item) => {
            const departments = String(item.department ?? '').split(',').filter((entry) => entry !== '');
            const matchesQuery = !query
                || (item.name ?? '').toLowerCase().includes(query)
                || (item.username ?? '').toLowerCase().includes(query);
            const matchesMobile = !mobile || String(item.mobile ?? '').toLowerCase().includes(mobile);
            // One box for Center and MHT ID, as on the sketch -- they are two ways of naming the
            // same person's posting and nobody searches both at once.
            const matchesCenter = !center
                || String(item.center ?? '').toLowerCase().includes(center)
                || String(item.mht_id ?? '').toLowerCase().includes(center);
            // A user with no departments is in all of them, so they match any department asked for.
            const matchesDepartment = wantedDepartments.length === 0
                || departments.length === 0
                || departments.some((id) => wantedDepartments.includes(id));
            const matchesRole = wantedRoles.length === 0 || wantedRoles.includes(String(item.role));
            // The superadmin is always active whatever the column says.
            const effectiveStatus = Number(item.role) === SUPERADMIN_ROLE ? '1' : String(item.status);
            const matchesStatus = status === '' || effectiveStatus === status;
            return matchesQuery && matchesMobile && matchesCenter && matchesDepartment && matchesRole && matchesStatus;
        }));
    }, [searchQuery, mobileQuery, centerQuery, departmentFilter, roleFilter, statusFilter, userList]);

    const getUserList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/user_master/list`);
            setUserList(response.data.data);
        } catch (error) {
            console.log(error);
            setLoadError(error?.response?.data?.msg ?? 'Could not load the user list.');
        }
    }

    const getOptions = async () => {
        try {
            const [departments, roles] = await Promise.all([
                axios.get(`${baseUrl}/department_master/list`),
                axios.get(`${baseUrl}/role_master/list`),
            ]);
            // Department 0 is "No Department", which is where a SCREEN waits for a real department
            // -- see backend/sql/no_department.sql. No user belongs to it, so it is not offered.
            setDepartmentOptions((departments.data?.data ?? [])
                .filter((department) => Number(department.id) !== 0)
                .map((department) => ({ value: department.id, label: department.name })));
            setRoleOptions((roles.data?.data ?? []).map((option) => ({ value: option.id, label: option.name })));
        } catch (error) {
            console.log(error);
        }
    }

    const handleClear = () => {
        setSearchQuery('');
        setDepartmentFilter([]);
        setRoleFilter([]);
        setMobileQuery('');
        setCenterQuery('');
        setStatusFilter({ value: '1', label: 'Active' });
    };

    const deleteUser = async (id) => {
        if (!window.confirm("Are you sure you want to disable this user? They will not be able to sign in.")) return;
        try {
            await axios.delete(`${baseUrl}/user_master/delete/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not disable the user.');
        }
    };

    const enableUser = async (id) => {
        if (!window.confirm("Enable this user?")) return;
        try {
            await axios.post(`${baseUrl}/user_master/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the user.');
        }
    };

    const destroyUser = async (id) => {
        if (!window.confirm("Permanently DELETE this user? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/user_master/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the user.');
        }
    };

    const openUser = (id, mode) => {
        navigate(`/user_master/${mode}/${btoa(id)}`, { state: userList.filter(item => item.id == id) });
    }

    const textFilter = (value, onChange, placeholder) => (
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg w-44 bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
        />
    );

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <div>
                    <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">User</h1>
                </div>
                <div>
                    {canCreate && <Link to="/user_master/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add User</button>
                    </Link>}
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-wrap gap-3 items-center">
                    {textFilter(searchQuery, setSearchQuery, 'Name / Login ID')}
                    <Select
                        {...menuAboveTable}
                        isMulti
                        classNamePrefix="select"
                        className="basic-multi-select min-w-56"
                        value={departmentFilter}
                        onChange={(selected) => setDepartmentFilter(selected ?? [])}
                        options={departmentOptions}
                        placeholder="Department"
                    />
                    <Select
                        {...menuAboveTable}
                        isMulti
                        classNamePrefix="select"
                        className="basic-multi-select min-w-48"
                        value={roleFilter}
                        onChange={(selected) => setRoleFilter(selected ?? [])}
                        options={roleOptions}
                        placeholder="Role"
                    />
                    {textFilter(mobileQuery, setMobileQuery, 'Mobile')}
                    {textFilter(centerQuery, setCenterQuery, 'Center / MHT ID')}
                    <Select
                        {...menuAboveTable}
                        classNamePrefix="select"
                        className="basic-multi-select w-36"
                        value={statusFilter}
                        onChange={(selected) => setStatusFilter(selected)}
                        options={[
                            { value: '', label: 'All' },
                            { value: '1', label: 'Active' },
                            { value: '0', label: 'Inactive' }
                        ]}
                        placeholder="Status"
                        isClearable={false}
                    />
                    <button
                        onClick={handleClear}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                    >
                        Clear
                    </button>
                </div>

                {loadError && <div className="text-red-500 pb-4">{loadError}</div>}

                {/* The header stays put while the rows scroll under it. The height cap is what
                    makes that work -- a container with no limit never scrolls, and a sticky
                    header has nothing to hold itself against. */}
                <div className="overflow-auto max-h-[65vh]">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Name</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Login ID</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Department</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Role</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Mobile</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Center</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">MHT ID</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Status</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => {
                            const isSuperadminUser = Number(item.role) === SUPERADMIN_ROLE;
                            return (
                            <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name || item.username}</th>
                                <td className="px-6 py-4">{item.username}</td>
                                {/* No departments means every department, which is what the sketch's "All" is. */}
                                <td className="px-6 py-4">{item.department_names || 'All'}</td>
                                <td className="px-6 py-4">{item.role_name ?? item.role}</td>
                                <td className="px-6 py-4">{item.mobile ?? '-'}</td>
                                <td className="px-6 py-4">{item.center ?? '-'}</td>
                                <td className="px-6 py-4">{item.mht_id ?? '-'}</td>
                                <td className="px-6 py-4">
                                    {item.status == 1 ? 'Active' : 'Inactive'}
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex gap-2">
                                        {canUpdate
                                            ? <button
                                                type="button"
                                                onClick={() => openUser(item.id, 'edit')}
                                                className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                                Edit
                                            </button>
                                            : <button
                                                type="button"
                                                onClick={() => openUser(item.id, 'view')}
                                                className="px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring focus:ring-gray-200">
                                                View
                                            </button>}
                                        {/* Screen Access is the SuperAdmin's alone. The route
                                            turns anyone else away, and the button does not offer
                                            them the trip. */}
                                        {isSuperadminSession && <Link to={`/user_master/screen_access/${btoa(item.id)}`}>
                                            <button
                                                type="button"
                                                className="px-3 py-1 text-sm bg-indigo-500 text-white rounded-md hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-200">
                                                Screen Access
                                            </button>
                                        </Link>}
                                        {/* The superadmin cannot be disabled or deleted -- it is the account that
                                            fixes a permissions mistake. Its status column says so. */}
                                        {!isSuperadminUser && item.status == 1 && canDisable && <button
                                            type="button"
                                            onClick={() => deleteUser(item.id)}
                                            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                            Disable
                                        </button>}
                                        {!isSuperadminUser && item.status == 0 && canActivate && <button
                                            type="button"
                                            onClick={() => enableUser(item.id)}
                                            className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                            Enable
                                        </button>}
                                        {!isSuperadminUser && canDelete && <button
                                            type="button"
                                            onClick={() => destroyUser(item.id)}
                                            className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                            Delete
                                        </button>}
                                    </div>
                                </td>
                            </tr>);
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

export default List;
